/**
 * Locations service
 * Handles CRUD operations for physical locations
 */

import { supabase } from "./supabase";
import type { Location, LocationAssignment } from "../types/organization.types";
import type {
  UUID,
  PaginatedResponse,
  PaginationParams,
} from "../types/common.types";

/**
 * Get all locations for an organization
 */
export const getLocations = async (
  organizationId: UUID,
  params?: PaginationParams,
): Promise<PaginatedResponse<Location>> => {
  const {
    page = 1,
    limit = 10,
    sortBy = "created_at",
    sortOrder = "desc",
  } = params || {};
  const start = (page - 1) * limit;
  const end = start + limit - 1;

  // Get total count
  const { count } = await supabase
    .from("locations")
    .select("*", { count: "exact", head: true })
    .eq("organization_id", organizationId)
    .eq("is_archived", false);

  // Get paginated data
  const { data, error } = await supabase
    .from("locations")
    .select("*")
    .eq("organization_id", organizationId)
    .eq("is_archived", false)
    .order(sortBy, { ascending: sortOrder === "asc" })
    .range(start, end);

  if (error) {
    throw error;
  }

  return {
    data: data as Location[],
    total: count || 0,
    page,
    limit,
    totalPages: Math.ceil((count || 0) / limit),
  };
};

/**
 * Search locations by name/city across ALL organizations (for pickers such as the segment
 * proximity anchor, which isn't scoped to one org). Distinct from org-scoped searchLocations below.
 * Wildcard ilike on name and city; capped at 20 results.
 */
export const searchLocationsGlobal = async (
  query: string,
): Promise<Location[]> => {
  const term = query.trim();
  if (!term) {
    return [];
  }
  const { data, error } = await supabase
    .from("locations")
    .select("*, organizations!inner(is_archived)")
    .eq("is_archived", false)
    .eq("organizations.is_archived", false)
    .or(`name.ilike.*${term}*,city.ilike.*${term}*`)
    .limit(20);
  if (error) {
    throw error;
  }
  return data as Location[];
};

/**
 * Get location by ID
 */
export const getLocation = async (id: UUID): Promise<Location> => {
  const { data, error } = await supabase
    .from("locations")
    .select("*")
    .eq("id", id)
    .single();

  if (error) {
    throw error;
  }
  if (!data) {
    throw new Error("Location not found");
  }

  return data as Location;
};

/**
 * Create new location
 */
export const createLocation = async (
  location: Omit<Location, "id" | "created_at" | "updated_at">,
): Promise<Location> => {
  const { data, error } = await supabase
    .from("locations")
    .insert(location)
    .select()
    .single();

  if (error) {
    throw error;
  }
  return data as Location;
};

/**
 * Update location
 */
export const updateLocation = async (
  id: UUID,
  updates: Partial<Location>,
): Promise<Location> => {
  const { data, error } = await supabase
    .from("locations")
    .update(updates)
    .eq("id", id)
    .select()
    .single();

  if (error) {
    throw error;
  }
  return data as Location;
};

/**
 * Delete location (soft delete)
 */
export const deleteLocation = async (id: UUID): Promise<void> => {
  const { error } = await supabase
    .from("locations")
    .update({ is_archived: true })
    .eq("id", id);

  if (error) {
    throw error;
  }
};

/**
 * Search locations by name or code
 */
export const searchLocations = async (
  organizationId: UUID,
  query: string,
): Promise<Location[]> => {
  const { data, error } = await supabase
    .from("locations")
    .select("*")
    .eq("organization_id", organizationId)
    .eq("is_archived", false)
    .or(`name.ilike.*${query}*,code.ilike.*${query}*`)
    .limit(20);

  if (error) {
    throw error;
  }
  return data as Location[];
};

/**
 * Get location assignments (users assigned to location)
 */
export const getLocationAssignments = async (
  locationId: UUID,
): Promise<LocationAssignment[]> => {
  const { data, error } = await supabase
    .from("location_assignments")
    .select("*, profiles(*)")
    .eq("location_id", locationId);

  if (error) {
    throw error;
  }
  return data as LocationAssignment[];
};

/**
 * Assign user to location
 */
export const assignUserToLocation = async (
  locationId: UUID,
  userId: UUID,
  isPrimary: boolean = false,
): Promise<LocationAssignment> => {
  const { data, error } = await supabase
    .from("location_assignments")
    .insert({
      location_id: locationId,
      user_id: userId,
      is_primary: isPrimary,
      assigned_at: new Date().toISOString(),
    })
    .select()
    .single();

  if (error) {
    throw error;
  }
  return data as LocationAssignment;
};

/**
 * Remove user from location
 */
export const removeUserFromLocation = async (
  locationId: UUID,
  userId: UUID,
): Promise<void> => {
  const { error } = await supabase
    .from("location_assignments")
    .delete()
    .eq("location_id", locationId)
    .eq("user_id", userId);

  if (error) {
    throw error;
  }
};

/**
 * Get locations by user ID
 * Checks user_organization_access table to determine which locations user can access
 */
export const getUserLocations = async (userId: UUID): Promise<Location[]> => {
  console.log("[getUserLocations] Fetching for user:", userId);

  // Get user's organization access
  const { data: accessData, error: accessError } = await supabase
    .from("user_organization_access")
    .select("organization_id, location_ids")
    .eq("user_id", userId);

  console.log("[getUserLocations] Access data:", { accessData, accessError });

  if (accessError) {
    console.error("[getUserLocations] Error fetching access:", accessError);
    throw accessError;
  }

  if (!accessData || accessData.length === 0) {
    console.log("[getUserLocations] No access data found for user");
    return [];
  }

  const allLocations: Location[] = [];

  // For each organization the user has access to
  for (const access of accessData) {
    console.log("[getUserLocations] Processing access:", {
      organization_id: access.organization_id,
      location_ids: access.location_ids,
    });

    if (access.location_ids === null || access.location_ids.length === 0) {
      // null or empty array means access to ALL locations in the organization
      console.log(
        "[getUserLocations] Fetching ALL locations for org:",
        access.organization_id,
      );

      const { data: orgLocations, error: locError } = await supabase
        .from("locations")
        .select("*, organization:organizations(id, name)")
        .eq("organization_id", access.organization_id)
        .order("name");

      console.log("[getUserLocations] Org locations result:", {
        orgLocations,
        locError,
      });

      if (locError) {
        console.error(
          "[getUserLocations] Error fetching org locations:",
          locError,
        );
        throw locError;
      }

      if (orgLocations) {
        allLocations.push(...(orgLocations as Location[]));
      }
    } else {
      // Specific location IDs
      console.log(
        "[getUserLocations] Fetching specific locations:",
        access.location_ids,
      );

      const { data: specificLocations, error: locError } = await supabase
        .from("locations")
        .select("*, organization:organizations(id, name)")
        .in("id", access.location_ids)
        .order("name");

      console.log("[getUserLocations] Specific locations result:", {
        specificLocations,
        locError,
      });

      if (locError) {
        console.error(
          "[getUserLocations] Error fetching specific locations:",
          locError,
        );
        throw locError;
      }

      if (specificLocations) {
        allLocations.push(...(specificLocations as Location[]));
      }
    }
  }

  console.log("[getUserLocations] Final locations:", allLocations);
  return allLocations;
};

/**
 * Get the partner's organization name when they have no locations (e.g. promoter orgs).
 * Returns null if the user has no org access record.
 */
export const getPartnerOrgName = async (
  userId: UUID,
): Promise<string | null> => {
  const { data } = await supabase
    .from("user_organization_access")
    .select("organizations(name)")
    .eq("user_id", userId)
    .single();
  return (data?.organizations as { name?: string } | null)?.name ?? null;
};

/**
 * Get active locations (status = active)
 */
export const getActiveLocations = async (
  organizationId: UUID,
): Promise<Location[]> => {
  const { data, error } = await supabase
    .from("locations")
    .select("*")
    .eq("organization_id", organizationId)
    .eq("status", "active")
    .order("name");

  if (error) {
    throw error;
  }
  return data as Location[];
};

// Shape of contact_locations join result
interface ContactLocationRow {
  location_id: UUID;
  locations: Location | null;
}

/**
 * Get locations for a contact
 */
export const getContactLocations = async (
  contactId: UUID,
): Promise<Location[]> => {
  const { data, error } = await supabase
    .from("contact_locations")
    .select("location_id, locations(*)")
    .eq("contact_id", contactId);

  if (error) {
    throw error;
  }
  const rows = (data || []) as ContactLocationRow[];
  return rows
    .map((item) => item.locations)
    .filter((loc): loc is Location => loc !== null);
};

/**
 * Link contact to locations (replaces existing associations)
 */
export const linkContactToLocations = async (
  contactId: UUID,
  locationIds: UUID[],
): Promise<void> => {
  // First, remove all existing associations
  await supabase.from("contact_locations").delete().eq("contact_id", contactId);

  // If no locations selected, we're done
  if (locationIds.length === 0) {
    return;
  }

  // Insert new associations
  const associations = locationIds.map((locationId) => ({
    contact_id: contactId,
    location_id: locationId,
  }));

  const { error } = await supabase
    .from("contact_locations")
    .insert(associations);

  if (error) {
    throw error;
  }
};

/**
 * Add contact to a location
 */
export const addContactToLocation = async (
  contactId: UUID,
  locationId: UUID,
): Promise<void> => {
  const { error } = await supabase.from("contact_locations").insert({
    contact_id: contactId,
    location_id: locationId,
  });

  if (error) {
    throw error;
  }
};

/**
 * Remove contact from a location
 */
export const removeContactFromLocation = async (
  contactId: UUID,
  locationId: UUID,
): Promise<void> => {
  const { error } = await supabase
    .from("contact_locations")
    .delete()
    .eq("contact_id", contactId)
    .eq("location_id", locationId);

  if (error) {
    throw error;
  }
};
